import React, { useMemo, useRef, forwardRef, useImperativeHandle, useCallback, useEffect, useState, useLayoutEffect } from 'react';
import { format, getDate, getDay, isSameDay, isWeekend, getDaysInMonth, startOfMonth, endOfMonth, addDays, addMonths, eachDayOfInterval, isBefore, startOfDay, getHours, getMinutes, set } from 'date-fns';
import { Calendar, RotateCcw, Plus, X } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import Joyride from 'react-joyride';
import { useCalendarTutorial } from '@/hooks/use-calendar-tutorial';
import { HourRange, Employee, DayShift, DayTemplate } from '@/types';
import { organizeShiftsIntoRows, ShiftRow } from '@/lib/scheduling/shift-utils';
import { getZIndex, getZIndexClass } from '@/lib/scheduling/z-index-layers';
import { EDITING_COLORS } from '@/lib/scheduling/editing-colors';
// React hooks and utilities
import { useBusinessTimeForCalendar } from '@/hooks/ui';
import { useDayExpansion } from '@/hooks/scheduling/useDayExpansion';
import { useHolidayData } from '@/hooks/scheduling/useHolidays';
import { useEventPositioning } from '@/hooks/scheduling/useEventPositioning';
import { useShiftErrorHandler } from '@/hooks/scheduling/useShiftErrorHandler';
import { useShiftCopying } from '@/hooks/scheduling/useShiftCopying';
import { useTimeCalculations } from '@/hooks/attendance/useTimeCalculations';
// Virtualization components
import VirtualizedDayList, { VirtualizedDayListRef } from './VirtualizedDayList';
import VirtualDragLayer from './VirtualDragLayer';

// Import components from shifts folder
import CalendarHeader from '../shifts/CalendarHeader';
import TimeHeader from '../shifts/TimeHeader';
import HourCell from '../shifts/HourCell';
import UnifiedActionsPanel from './UnifiedActionsPanel';
// Import shift creation dialog components
import ShiftBuilderDialog from '../shifts/InlineShiftBuilder';
import TemplateCreationDialog from '../shifts/TemplateCreationDialog';
import ShiftCreationOverlay from '../shifts/ShiftCreationOverlay';
import QuickShiftCreator from '../shifts/QuickShiftCreator';


import { BaseCalendarProps, ShiftCalendarRef, EventWithAssignments } from './types';
import { ROW_HEIGHT_PX, INTERVAL_MINUTES, MINUTES_PER_HOUR, INTERVALS_PER_HOUR, DAY_COLUMN_WIDTH_PX, GAP_HEIGHT_PX, MONTH_DIVIDER_VISUAL_HEIGHT_PX } from '../shifts/constants';
import DraggableShift from '../shifts/DraggableShift';
import { RelativeBreak } from '@/types';
import { applyTemplateToDate } from '@/lib/scheduling/day-template-utils';
import { useCalendarLogic } from './hooks/useCalendarLogic';
import { useShiftCalendar } from './ShiftCalendarProvider';
import { usePreparationStatusFromContext, getPreparationStatusClass, type VisibleDateRange } from '@/hooks/scheduling/usePreparationStatusFromContext';
// useBusinessClosedDates is now accessed via ShiftDataProvider context (useShiftData)
import { getNonHistoricalShiftsForDate } from '@/lib/scheduling/shift-filtering-utils';
import { useUpdateShift, useCreateShift } from '@/hooks/scheduling/useShiftMutations';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { useShiftUI } from '@/components/shifts/context/ShiftUIProvider';
import { useUnifiedShift } from '@/components/shifts/context/ComposedShiftProvider';
// Removed scrollbar utilities since we're now using Radix ScrollArea
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { GRID_DRAG_STYLES } from '@/lib/scheduling/grid-drag-integration';
// Import visual fixes CSS for shift calendar rendering and z-index layering
import '../shifts/shift-calendar-visual-fixes.css';
// Import components for simple non-virtualized rendering
import DayRow from '../shifts/DayRow';
import VirtualizedShiftLayer from './VirtualizedShiftLayer';

// Consistent calendar height and base styling for full-height mode
const CALENDAR_HEIGHT = "h-[calc(100vh-80px)]";
const CALENDAR_BASE_CLASSES = "border rounded-lg bg-card text-card-foreground shadow-sm";

// Calendar variant configuration type
export interface CalendarVariantConfig {
  hideDayColumn: boolean;
  hideActionsColumn: boolean;
  hideSidePanels: boolean;
  hideTimeHeader: boolean;
  areSidePanelsCollapsed: boolean;
  expandAllDays: boolean;
  enableShiftManagement: boolean;
  autoScrollToCurrentTime: boolean;
  hideZoomControls: boolean;
  hideControls: boolean;
}

// Predefined configurations for each variant
export const CALENDAR_VARIANTS: Record<string, CalendarVariantConfig> = {
  full: {
    hideDayColumn: false,
    hideActionsColumn: false,
    hideSidePanels: false,
    hideTimeHeader: false,
    areSidePanelsCollapsed: false,
    expandAllDays: false,
    enableShiftManagement: true,
    autoScrollToCurrentTime: true,
    hideZoomControls: false,
    hideControls: false,
  },
  insights: {
    hideDayColumn: true,
    hideActionsColumn: true,
    hideSidePanels: true,
    hideTimeHeader: false,
    areSidePanelsCollapsed: true,
    expandAllDays: true,
    enableShiftManagement: false,
    autoScrollToCurrentTime: true,
    hideZoomControls: true,
    hideControls: true,
  },
  timetracking: {
    hideDayColumn: true,
    hideActionsColumn: false,
    hideSidePanels: true,
    hideTimeHeader: false,
    areSidePanelsCollapsed: true,
    expandAllDays: false,
    enableShiftManagement: false,
    autoScrollToCurrentTime: false,
    hideZoomControls: true,
    hideControls: true,
  },
};

export type CalendarVariant = keyof typeof CALENDAR_VARIANTS;

interface BaseShiftCalendarProps extends BaseCalendarProps {
  // Calendar variant - provides predefined configurations
  variant?: CalendarVariant;
  
  // Display control props (can override variant defaults)
  displayOnlyDay?: Date | null;
  hidePastDays?: boolean;
  expandAllDays?: boolean;
  fullHeight?: boolean;
  
  // UI control props (can override variant defaults)
  hideTimeHeader?: boolean;
  hideDayColumn?: boolean;
  hideActionsColumn?: boolean;
  hideZoomControls?: boolean;
  hideControls?: boolean;
  hideStatusIndicators?: boolean;
  hideSidePanels?: boolean;
  areSidePanelsCollapsed?: boolean;
  enableShiftManagement?: boolean;
  isEditingMode?: boolean;
  
  // Event handlers
  onClick?: () => void;
  onDaySelect?: (date: Date) => void;
  onShiftCreated?: () => void;
  refetchShifts?: () => void; // Targeted refetch for just shifts data
  onAssignEmployee?: (shiftId: string, employeeId: string | null) => void;
  
  // Hover handlers for time tracking
  hoveredShiftId?: string | null;
  onShiftHover?: (shiftId: string | null) => void;
  
  // Navigation props (optional for variants that don't need them)
  onPreviousMonth?: () => void;
  onNextMonth?: () => void;
  onSetToday?: () => void;
  onToggleHidePastDays?: (checked: boolean) => void;
  onToggleExpandAllDays?: (checked: boolean) => void;
  onSyncCalendar?: () => void;
  onMonthSelect?: (date: Date) => void;
  onToggleSidePanelsCollapsed?: () => void;
  
  // Business props
  businessName?: string;
  businessCreatedAt?: string;
  selectedEditingDate?: Date | null;
  
  // Advanced features
  events?: EventWithAssignments[];
  showEvents?: boolean;
  onToggleEvents?: () => void;
  hideActionButtons?: boolean;
  customActionButton?: React.ReactNode;
  autoScrollToCurrentTime?: boolean;
  autoFitToScreen?: boolean;
  
  // Context data props (optional - allows component to work without context)
  loadingShifts?: boolean;
  loadingEmployees?: boolean;
  loadingDisplayHours?: boolean;
  smartDisplayHours?: {
    range: any;
    calculationMethod: string;
    canExpand: boolean;
    expandRange: (newRange: any) => void;
    resetToAuto: () => void;
    isLoading: boolean;
  };
  
  // Time range control props
  show24Hours?: boolean;
  onToggleShow24Hours?: (checked: boolean) => void;

  // Infinite scroll mode props
  scrollMode?: 'monthly' | 'infinite';
  /** Days array for infinite scroll mode (from InfiniteScrollShiftDataProvider) */
  infiniteScrollDays?: Date[];
  /** Whether infinite scroll mode is active */
  isInfiniteScrollMode?: boolean;

  // Shift-aware time range props
  enableShiftAwareTimeRange?: boolean;
  
  // Actions panel props
  isActionsPanelCollapsed?: boolean;
  onToggleActionsPanel?: () => void;
  unifiedActionsPanelMode?: import('./types').UnifiedActionsPanelMode;
  onUnifiedActionsPanelModeChange?: (mode: import('./types').UnifiedActionsPanelMode) => void;
  // Template data props
  shiftTemplates?: import('./types').ShiftTemplate[];
  loadingShiftTemplates?: boolean;
  dayTemplates?: import('@/types').DayTemplate[];
  loadingDayTemplates?: boolean;
  
  // Template handlers
  onEditShiftTemplate?: (templateId: string) => void;
  onDeleteShiftTemplate?: (templateId: string) => Promise<void>;
  onEditDayTemplate?: (templateId: string) => void;
  onDeleteDayTemplate?: (templateId: string) => void;
  onSaveDayTemplate?: (name: string, description: string) => Promise<void>;
  
  // Shift editing mode props
  selectedShiftIds?: Set<string>;
  onShiftSelect?: (shiftId: string, selected: boolean) => void;
  onSelectAllShifts?: () => void;
  onClearShiftSelection?: () => void;
  onEditShift?: (shiftId: string) => void;
  onDeleteShift?: (shiftId: string) => void;
  onCopyShifts?: (shiftIds: string[]) => void;
  onSaveShiftsAsTemplate?: (shiftIds: string[], name: string, description?: string) => void;

}

const BaseShiftCalendar = forwardRef<ShiftCalendarRef, BaseShiftCalendarProps>(({
  className,
  onClick,
  variant = 'full',
  currentDisplayMonth,
  shifts,
  workingHours,
  openingHours,
  displayStartTime,
  displayEndTime,
  businessId,
  employees,
  loading = false,
  countryCode,
  displayOnlyDay,
  hidePastDays,
  expandAllDays,
  fullHeight = false,
  hideTimeHeader,
  hideDayColumn,
  hideActionsColumn,
  hideZoomControls,
  hideControls,
  hideStatusIndicators,
  hideSidePanels,
  areSidePanelsCollapsed,
  enableShiftManagement,
  isEditingMode = false,
  onDaySelect,
  onShiftCreated,
  refetchShifts,
  onAssignEmployee,
  hoveredShiftId,
  onShiftHover,
  onPreviousMonth,
  onNextMonth,
  onSetToday,
  onToggleHidePastDays,
  onToggleExpandAllDays,
  onSyncCalendar,
  onMonthSelect,
  onToggleSidePanelsCollapsed,
  businessName,
  businessCreatedAt,
  selectedEditingDate,
  events,
  showEvents,
  onToggleEvents,
  hideActionButtons,
  customActionButton,
  autoScrollToCurrentTime,
  autoFitToScreen,
  // Time range control props
  show24Hours,
  onToggleShow24Hours,
  // Infinite scroll mode props
  scrollMode = 'monthly',
  infiniteScrollDays,
  isInfiniteScrollMode = false,
  // Shift-aware time range props
  enableShiftAwareTimeRange = true,
  // Actions panel props
  isActionsPanelCollapsed,
  onToggleActionsPanel,
  unifiedActionsPanelMode,
  onUnifiedActionsPanelModeChange,
  shiftTemplates,
  loadingShiftTemplates,
  onEditShiftTemplate,
  onDeleteShiftTemplate,
  dayTemplates,
  loadingDayTemplates,
  onEditDayTemplate,
  onDeleteDayTemplate,
  onSaveDayTemplate,
  selectedShiftIds,
  onShiftSelect,
  onSelectAllShifts,
  onClearShiftSelection,
  onEditShift,
  onDeleteShift,
  onCopyShifts,
  onSaveShiftsAsTemplate,
  // Context data props (optional)
  loadingShifts: propsLoadingShifts,
  loadingEmployees: propsLoadingEmployees,
  loadingDisplayHours: propsLoadingDisplayHours,
  smartDisplayHours: propsSmartDisplayHours
}, ref) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const calendarHeaderRef = useRef<HTMLDivElement>(null);
  const dayRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const cardContentRef = useRef<HTMLDivElement>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const virtualizedListRef = useRef<VirtualizedDayListRef>(null);
  const [isManualZoom, setIsManualZoom] = useState(false);

  // Historical edit protection
  const { isShiftLocked, isHistoricalEditEnabled, recordHistoricalEdit } = useHistoricalEdit();

  // Quick copy / placing mode from UI context
  const { isPlacingMode, placingShift, cancelPlacingMode } = useShiftUI();

  // State for measured container width (for accurate hourCellWidthPx calculation)
  const [measuredContainerWidth, setMeasuredContainerWidth] = useState<number | null>(null);

  // State for day template application prompt
  const [selectedTemplateForApplication, setSelectedTemplateForApplication] = useState<DayTemplate | null>(null);
  const [targetDateForTemplateApplication, setTargetDateForTemplateApplication] = useState<Date | null>(null);
  const [isTemplatePopoverOpen, setIsTemplatePopoverOpen] = useState(false);
  
  // State for planning preview shifts from dropped templates
  const [planningPreviewShifts, setPlanningPreviewShifts] = useState<DayShift[]>([]);

  // State for visible month in infinite scroll mode (sticky month indicator)
  const [visibleMonth, setVisibleMonth] = useState<Date>(currentDisplayMonth);
  // State for iOS-style sticky month push offset
  const [stickyMonthOffset, setStickyMonthOffset] = useState(0);

  // Callback for VirtualizedDayList to report visible month changes
  const handleVisibleMonthChange = useCallback((month: Date) => {
    setVisibleMonth(month);
  }, []);

  // Callback for VirtualizedDayList to report sticky month push offset
  const handleStickyMonthUpdate = useCallback((offset: number) => {
    setStickyMonthOffset(offset);
  }, []);

  // Refs for controlling UI state from tutorial
  const calendarHeaderControlRef = useRef<{
    openFilter: () => void;
    closeFilter: () => void;
    openSettings: () => void;
    closeSettings: () => void;
  } | null>(null);

  // Tutorial state management with callbacks
  const {
    run: tutorialRun,
    stepIndex: tutorialStepIndex,
    steps: tutorialSteps,
    handleJoyrideCallback,
    startTutorial,
    stopTutorial,
  } = useCalendarTutorial({
    openFilter: () => calendarHeaderControlRef.current?.openFilter(),
    closeFilter: () => calendarHeaderControlRef.current?.closeFilter(),
    openSettings: () => calendarHeaderControlRef.current?.openSettings(),
    closeSettings: () => calendarHeaderControlRef.current?.closeSettings(),
    openActionsPanel: () => {
      if (isActionsPanelCollapsed && onToggleActionsPanel) {
        onToggleActionsPanel();
      }
    },
    closeActionsPanel: () => {
      if (!isActionsPanelCollapsed && onToggleActionsPanel) {
        onToggleActionsPanel();
      }
    },
    setActionsPanelMode: (mode) => {
      if (onUnifiedActionsPanelModeChange) {
        onUnifiedActionsPanelModeChange(mode);
      }
    },
  });

  // Function to generate planning preview shifts from a template
  const generatePlanningPreviewShifts = useCallback((template: DayTemplate, targetDate: Date): DayShift[] => {
    return template.shifts.map((templateShift, index) => {
      // Parse time from template shift (format: "HH:MM")
      const [startHour, startMinute] = templateShift.startTime.split(':').map(Number);
      const [endHour, endMinute] = templateShift.endTime.split(':').map(Number);
      
      // Create date objects for the target date
      const startTime = set(targetDate, { hours: startHour, minutes: startMinute, seconds: 0, milliseconds: 0 });
      const endTime = set(targetDate, { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });
      
      // Handle shifts that cross midnight
      const adjustedEndTime = endTime < startTime ? addDays(endTime, 1) : endTime;
      
      return {
        id: `planning-preview-${template.id}-${index}-${Date.now()}`, // Unique temporary ID
        business_id: template.business_id || businessId,
        title: templateShift.title || 'Untitled Shift',
        startTime,
        endTime: adjustedEndTime,
        employeeId: null, // No employee assigned in planning mode
        employeeName: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        isNew: false,
        isPlanningPreview: true,
        isPreview: true, // Also mark as preview for backward compatibility
        isHistorical: false
      } as DayShift;
    });
  }, [businessId]);

  // Function to clear planning preview shifts
  const clearPlanningPreviews = useCallback(() => {
    setPlanningPreviewShifts([]);
  }, []);

  // Measure actual container width and height using ResizeObserver for accurate timing
  // Use callback ref pattern for immediate attachment without delays
  const scrollContainerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    // Clean up previous observer if it exists
    if ((scrollContainerRef as any)._resizeObserver) {
      (scrollContainerRef as any)._resizeObserver.disconnect();
      (scrollContainerRef as any)._resizeObserver = null;
    }

    // Update the ref
    (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;

    // Attach new observer if node exists
    if (node) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          const height = entry.contentRect.height;

          // Directly update width - scrollbar is now hidden so no need for threshold filtering
          setMeasuredContainerWidth(width);

          // Only set height for full variant (used for virtualization)
          // insights and timetracking variants don't use virtualization
          if (variant === 'full') {
            setContainerHeight(height);
          }
        }
      });

      resizeObserver.observe(node);
      (scrollContainerRef as any)._resizeObserver = resizeObserver;
    }
  }, []);

  // Use props if provided, otherwise fallback to loading=false for backwards compatibility
  const loadingShifts = propsLoadingShifts ?? false;
  const loadingEmployees = propsLoadingEmployees ?? false;
  const loadingDisplayHours = propsLoadingDisplayHours ?? false;
  const smartDisplayHours = propsSmartDisplayHours ?? {
    range: { start: 8, end: 18 },
    calculationMethod: 'fallback',
    canExpand: false,
    expandRange: () => {},
    resetToAuto: () => {},
    isLoading: false
  };
  
  // Shift creation dialog state management - using ref to prevent stale closure issues
  const [templateCreationStartTime, setTemplateCreationStartTimeState] = useState<Date | null>(null);
  const [templateCreationEndTime, setTemplateCreationEndTime] = useState<Date | null>(null);
  const templateCreationStartTimeRef = useRef<Date | null>(null);

  // **CURSOR FOLLOW FIX**: Track current cursor position for live preview
  const [templateCreationCursorTime, setTemplateCreationCursorTime] = useState<Date | null>(null);

  // Unified setter that updates both state and ref
  const setTemplateCreationStartTime = useCallback((newValue: Date | null) => {
    templateCreationStartTimeRef.current = newValue;
    setTemplateCreationStartTimeState(newValue);
  }, []);
  const [templateCreationRowIndex, setTemplateCreationRowIndex] = useState<number | null>(null);
  const [templateCreationDayIndex, setTemplateCreationDayIndex] = useState<number | null>(null); // Track which day was clicked
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [isResizeMode, setIsResizeMode] = useState(false);
  const [showQuickCreateDialog, setShowQuickCreateDialog] = useState(false);
  
  
  
  // Apply variant configuration, allowing individual props to override
  const variantConfig = CALENDAR_VARIANTS[variant];
  const effectiveProps = useMemo(() => ({
    hideDayColumn: hideDayColumn ?? variantConfig.hideDayColumn,
    hideActionsColumn: hideActionsColumn ?? variantConfig.hideActionsColumn,
    hideSidePanels: hideSidePanels ?? variantConfig.hideSidePanels,
    hideTimeHeader: hideTimeHeader ?? variantConfig.hideTimeHeader,
    areSidePanelsCollapsed: areSidePanelsCollapsed ?? variantConfig.areSidePanelsCollapsed,
    expandAllDays: expandAllDays ?? variantConfig.expandAllDays,
    enableShiftManagement: enableShiftManagement ?? variantConfig.enableShiftManagement,
    autoScrollToCurrentTime: autoScrollToCurrentTime ?? variantConfig.autoScrollToCurrentTime,
    hideZoomControls: hideZoomControls ?? variantConfig.hideZoomControls,
    hideControls: hideControls ?? variantConfig.hideControls,
    hideStatusIndicators: hideStatusIndicators ?? false,
    hidePastDays: hidePastDays ?? false,
  }), [
    variant,
    hideDayColumn,
    hideActionsColumn,
    hideSidePanels,
    hideTimeHeader,
    areSidePanelsCollapsed,
    expandAllDays,
    enableShiftManagement,
    autoScrollToCurrentTime,
    hideZoomControls,
    hideControls,
    hideStatusIndicators,
    hidePastDays,
    variantConfig
  ]);
  
  const { currentTime: currentTimeState, businessTimezone } = useBusinessTimeForCalendar();

  // Use ref to store currentTime to avoid triggering rerenders every 60 seconds
  // This prevents cascading rerenders through the entire calendar tree
  const currentTimeRef = useRef(currentTimeState);
  useEffect(() => {
    currentTimeRef.current = currentTimeState;
  }, [currentTimeState]);

  // For backwards compatibility, create a getter that uses the ref
  const getCurrentTime = useCallback(() => currentTimeRef.current, []);

  // Error handling and shift management (only for full variant)
  const { handleError, handleSuccess } = useShiftErrorHandler();
  
  // Shift copying functionality (only for full variant)
  const { copiedShift, setCopiedShift, pasteShift } = useShiftCopying({
    businessId,
    onShiftCreated: effectiveProps.enableShiftManagement ? onShiftCreated : undefined
  });

  // TanStack Query mutation hooks for optimistic updates on shift create/update operations
  const updateShift = useUpdateShift();
  const createShiftMutation = useCreateShift();

  // Compute visible date range for infinite scroll mode
  // This ensures day status is calculated for ALL visible days, not just the selected month
  const visibleDateRange = useMemo((): VisibleDateRange | undefined => {
    if (!isInfiniteScrollMode || !infiniteScrollDays || infiniteScrollDays.length === 0) {
      return undefined;
    }
    return {
      startDate: infiniteScrollDays[0],
      endDate: infiniteScrollDays[infiniteScrollDays.length - 1]
    };
  }, [isInfiniteScrollMode, infiniteScrollDays]);

  // Use context-based preparation status - calculates from shifts already in context
  // NO API calls, automatically updates when shifts change
  // Pass currentDisplayMonth to ensure day status is calculated for the viewed month
  // Pass visibleDateRange to support infinite scroll mode with multiple months visible
  const { getDayStatus, isLoading: isDayStatusLoading } = usePreparationStatusFromContext(currentDisplayMonth, visibleDateRange);

  // Wrapper for backward compatibility with existing components
  const getDayStatusByKey = useCallback((dateKey: string) => {
    return getDayStatus(dateKey);
  }, [getDayStatus]);

  // Get custom closed dates from ShiftDataProvider context
  // This ensures real-time updates when DayGroupActions modifies closed dates
  const {
    closedDatesMap,
    addClosedDate,
    removeClosedDate,
    isDateClosed,
    checkExistingShifts
  } = useUnifiedShift();

  // Use calendar logic hook with shift-aware time range calculation
  const {
    effectiveDisplayStartTime,
    effectiveDisplayEndTime,
    totalDisplayHours,
    allDaysInSelectedMonth,
    daysInSelectedMonth,
    shiftsByDay: getShiftsByDay
  } = useCalendarLogic({
    currentDisplayMonth,
    displayOnlyDay,
    hidePastDays: effectiveProps.hidePastDays,
    displayStartTime,
    displayEndTime,
    show24Hours,
    shifts, // Pass shift data for shift-aware time range calculation
    enableShiftAwareTimeRange, // Use the prop to control shift-aware behavior
    // Infinite scroll support
    infiniteScrollDays,
    isInfiniteScrollMode
  });

  // Display hours now come from useSimpleDisplayHours via ShiftDataProvider
  // Safe fallbacks to prevent null state during month switches
  const safeDisplayStartTime = useMemo(() => {
    if (displayStartTime instanceof Date) return displayStartTime;
    // Fallback to prevent null - uses default 6:00 AM
    return new Date(1970, 0, 1, 6, 0, 0);
  }, [displayStartTime]);

  const safeDisplayEndTime = useMemo(() => {
    if (displayEndTime instanceof Date) return displayEndTime;
    // Fallback to prevent null - uses default 10:00 PM
    return new Date(1970, 0, 1, 22, 0, 0);
  }, [displayEndTime]);

  // Legacy compatibility flags
  const displayTimesReady = displayStartTime instanceof Date && displayEndTime instanceof Date;
  const isLoading = loadingShifts || loadingEmployees || loadingDisplayHours;

  // Use time calculations hook  
  const { effectiveDisplayStartTime: timeCalcStart, effectiveDisplayEndTime: timeCalcEnd, totalDisplayHours: timeCalcHours, getDisplayTimesForDay } = useTimeCalculations({
    displayStartTime: effectiveDisplayStartTime,
    displayEndTime: effectiveDisplayEndTime,
    openingHours: openingHours || []
  });

  // When explicit display times exist from shift-aware calculation, respect them
  // useTimeCalculations should only be a fallback when no explicit times are available
  // Safe fallbacks ensure we NEVER have null display times (prevents stuck loader on month switch)
  const finalDisplayStartTime = effectiveDisplayStartTime || timeCalcStart || safeDisplayStartTime;
  const finalDisplayEndTime = effectiveDisplayEndTime || timeCalcEnd || safeDisplayEndTime;
  const finalTotalDisplayHours = totalDisplayHours || timeCalcHours || 16; // 16 hours fallback (6 AM - 10 PM)

  // Get holidays for the current display period (must be before calendarReadiness)
  const currentYear = currentDisplayMonth.getFullYear();
  const holidayYears = useMemo(() => [currentYear - 1, currentYear, currentYear + 1], [currentYear]);
  const { isHoliday: checkIsHoliday, loading: holidaysLoading } = useHolidayData({
    countryCode,
    years: holidayYears,
    enabled: !!countryCode
  });

  // Single source of truth for calendar readiness - placed AFTER final display times are calculated
  const calendarReadiness = useMemo(() => {
    // Check final display times (with fallbacks) - these should always be valid Date objects
    const hasDisplayTimes = finalDisplayStartTime instanceof Date && finalDisplayEndTime instanceof Date;
    const hasShiftsData = !loadingShifts; // Shifts query complete (even if empty)
    const hasEmployeesData = !loadingEmployees;
    // Opening hours are optional - undefined/null is acceptable (not all calendars need them)
    const hasOpeningHours = openingHours === undefined || openingHours === null || Array.isArray(openingHours);
    // Day status must be ready to show correct border colors (green/orange/red)
    const hasDayStatusData = !isDayStatusLoading;
    // Holidays must be loaded to correctly display holiday indicators on days
    const hasHolidaysData = !holidaysLoading;

    return {
      displayTimesReady: hasDisplayTimes,
      shiftsReady: hasShiftsData,
      employeesReady: hasEmployeesData,
      openingHoursReady: hasOpeningHours,
      dayStatusReady: hasDayStatusData,
      holidaysReady: hasHolidaysData,
      isFullyReady: hasDisplayTimes && hasShiftsData && hasEmployeesData && hasOpeningHours && hasDayStatusData && hasHolidaysData,
    };
  }, [finalDisplayStartTime, finalDisplayEndTime, loadingShifts, loadingEmployees, openingHours, isDayStatusLoading, holidaysLoading]);

  // Use final display hours directly - no complex expansion logic
  const actualDisplayHours = finalTotalDisplayHours;

  // Hour cell width calculation - used for both shift positioning AND CSS grid columns
  // This ensures perfect alignment between draggable shifts and grid cells
  const hourCellWidthPx = useMemo(() => {
    if (!actualDisplayHours || actualDisplayHours <= 0) return 0;
    if (!measuredContainerWidth) return 0;

    const totalIntervals = Math.ceil(actualDisplayHours * INTERVALS_PER_HOUR);
    const dayColumnWidth = effectiveProps.hideDayColumn ? 0 : 32;
    const availableWidthForIntervals = measuredContainerWidth - dayColumnWidth;

    return availableWidthForIntervals / totalIntervals;
  }, [actualDisplayHours, measuredContainerWidth, effectiveProps.hideDayColumn]);

  // Organize shifts by day
  const shiftsByDay = useMemo(() => {
    const result = getShiftsByDay(shifts);
    return result;
  }, [getShiftsByDay, shifts, businessId, currentDisplayMonth]);

  // Create combined shifts data that includes both base shifts and planning preview shifts
  // This is used by all layout calculations to ensure consistent sizing
  const combinedShiftsByDay = useMemo(() => {
    const combined = { ...shiftsByDay };
    
    planningPreviewShifts.forEach(shift => {
      const dayOfMonth = getDate(shift.startTime);
      if (!combined[dayOfMonth]) {
        combined[dayOfMonth] = [];
      }
      if (!combined[dayOfMonth].some(existing => existing.id === shift.id)) {
        combined[dayOfMonth] = [...combined[dayOfMonth], shift];
      }
    });
    
    return combined;
  }, [shiftsByDay, planningPreviewShifts]);

  // Day expansion logic
  const { expandedDayKeys, toggleDayExpansion } = useDayExpansion({ 
    daysInSelectedMonth, 
    expandAllDays: effectiveProps.expandAllDays 
  });

  // Use event positioning hook
  const { eventsByDay, eventPositionsWithLayout, eventsLookup } = useEventPositioning({
    events,
    showEvents,
    daysInSelectedMonth: allDaysInSelectedMonth,
    effectiveDisplayStartTime: finalDisplayStartTime,
    effectiveDisplayEndTime: finalDisplayEndTime,
    totalDisplayHours: actualDisplayHours,
    hourCellWidthPx
  });

  // Removed dayRowOffsetMap - now using real-time calculation like day labels

  // Organize shifts into rows for each day when that day is expanded
  const shiftsByDayOrganized = useMemo(() => {
    const organizedMap: { [key: number]: ShiftRow[] } = {};
    for (const day in combinedShiftsByDay) {
      const dayOfMonth = parseInt(day, 10);
      // Use allDaysInSelectedMonth to include all days with shifts (filtering happens at shift level)
      const dayDate = allDaysInSelectedMonth.find(d => getDate(d) === dayOfMonth);
      
      if (!dayDate) continue;
      
      const dayKey = format(dayDate, 'yyyy-MM-dd');

      if (expandedDayKeys.includes(dayKey)) {
        organizedMap[day] = organizeShiftsIntoRows(combinedShiftsByDay[day]);
      } else {
        organizedMap[day] = [{ rowIndex: 0, shifts: combinedShiftsByDay[day] }];
      }
    }
    
    return organizedMap;
  }, [combinedShiftsByDay, expandedDayKeys, allDaysInSelectedMonth, planningPreviewShifts]);

  // Memoize hidden days offset calculation for performance
  // Removed hiddenDaysOffset - no longer needed with separate past/upcoming grid approach

  const openingHoursByDayOfWeek = useMemo(() => {
    if (!openingHours || !Array.isArray(openingHours)) {
      return new Map();
    }

    // Create map using day_of_week as key for raw opening hours format
    const mappedHours = new Map();
    
    openingHours.forEach(oh => {
      // Handle different formats: raw opening hours (day_of_week) vs HourRange (startTime)
      if (typeof oh.day_of_week === 'string') {
        // Raw opening hours format - map day names to numbers (0=Sunday, 1=Monday, etc.)
        const dayNameMap: { [key: string]: number } = {
          'Sunday': 0,
          'Monday': 1,
          'Tuesday': 2,
          'Wednesday': 3,
          'Thursday': 4,
          'Friday': 5,
          'Saturday': 6
        };

        const dayNumber = dayNameMap[oh.day_of_week];
        if (dayNumber !== undefined) {
          mappedHours.set(dayNumber, oh);
        }
      } else if (typeof oh.day_of_week === 'number') {
        // Already numeric format - use directly
        mappedHours.set(oh.day_of_week, oh);
      } else if (oh.startTime) {
        // HourRange format - map by day name
        mappedHours.set(format(oh.startTime, 'EEEE'), oh);
      }
    });


    return mappedHours;
  }, [openingHours, businessId]);

  // Shift management functions (only enabled for full variant)
  const handleCreateShift = useCallback((businessId: string, startTime: Date, endTime: Date, title: string) => {
    if (!effectiveProps.enableShiftManagement) return;

    // Check if the start time is in the past and protected
    if (isShiftLocked(startTime, false)) {
      toast.error('Cannot create shift in the past', {
        description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
      });
      return;
    }

    // Use mutation for optimistic updates - shift appears immediately with temp ID
    createShiftMutation.mutate({
      businessId,
      startTime,
      endTime,
      title: title || 'New Shift',
    }, {
      onSuccess: async (createdShift) => {
        // Session-level historical edit tracking (for triangle indicator)
        const isCreatedRetroactively = isBefore(startOfDay(startTime), startOfDay(new Date()));
        if (isCreatedRetroactively && isHistoricalEditEnabled) {
          await recordHistoricalEdit(
            createdShift.id,
            businessId,
            'created_retroactively',
            null,
            { startTime: startTime.toISOString(), endTime: endTime.toISOString(), title: title || 'New Shift' }
          );
        }
        handleSuccess('Shift created successfully');
        onShiftCreated?.();
      },
    });
  }, [createShiftMutation, effectiveProps.enableShiftManagement, isShiftLocked, isHistoricalEditEnabled, recordHistoricalEdit, handleSuccess, onShiftCreated]);

  const handleRepositionShift = useCallback((shiftId: string, newStartTime: Date, newEndTime: Date) => {
    if (!effectiveProps.enableShiftManagement) return;

    // Use mutation for optimistic updates - shift stays in new position immediately
    updateShift.mutate({
      id: shiftId,
      startTime: newStartTime,
      endTime: newEndTime,
    }, {
      onSuccess: async () => {
        // Session-level historical edit tracking (for triangle indicator)
        // Note: The mutation service already handles database-level historical tracking
        const isRepositionedToPast = isBefore(startOfDay(newStartTime), startOfDay(new Date()));
        if (isRepositionedToPast && isHistoricalEditEnabled && businessId) {
          await recordHistoricalEdit(shiftId, businessId, 'times_changed', null, {
            startTime: newStartTime.toISOString(),
            endTime: newEndTime.toISOString()
          });
        }
      },
    });
  }, [updateShift, effectiveProps.enableShiftManagement, isHistoricalEditEnabled, businessId, recordHistoricalEdit]);

  const handleClearAllShifts = useCallback(async (date: Date) => {
    if (!effectiveProps.enableShiftManagement || !businessId) return;

    // Check if the date is protected
    if (isShiftLocked(date, false)) {
      toast.error('Cannot clear shifts on a protected date', {
        description: 'Enable "Editing Past" mode in the calendar header to clear shifts on historical dates.'
      });
      return;
    }

    try {
      const startOfSelectedDay = new Date(date);
      startOfSelectedDay.setHours(0, 0, 0, 0);

      const endOfSelectedDay = new Date(date);
      endOfSelectedDay.setHours(23, 59, 59, 999);

      const { data: shiftsToDelete, error: fetchError } = await supabaseClient
        .from('shifts')
        .select('id')
        .eq('business_id', businessId)
        .gte('start_time', startOfSelectedDay.toISOString())
        .lt('start_time', endOfSelectedDay.toISOString());

      if (fetchError) {
        throw fetchError;
      }

      if (!shiftsToDelete || shiftsToDelete.length === 0) {
        handleSuccess('No shifts to clear for this day');
        return;
      }

      // Direct database delete operation for immediate UI updates
      const { error: deleteError } = await supabaseClient
        .from('shifts')
        .delete()
        .in('id', shiftsToDelete.map(shift => shift.id));

      if (deleteError) throw deleteError;

      handleSuccess(`${shiftsToDelete.length} shift(s) cleared successfully`);
      if (onShiftCreated) {
        onShiftCreated();
      }
    } catch (error) {
      handleError('clear shifts', error);
    }
  }, [businessId, onShiftCreated, handleError, handleSuccess, effectiveProps.enableShiftManagement, isShiftLocked]);

  const handleApplyDayTemplate = useCallback(async (templateOrId: string | DayTemplate, targetDate: Date, replace: boolean) => {
    if (!businessId || !effectiveProps.enableShiftManagement) return;

    let template: DayTemplate | undefined;
    if (typeof templateOrId === 'string') {
      // Legacy support: find template by ID
      template = dayTemplates?.find(t => t.id === templateOrId);
      if (!template) {
        toast.error('Template not found');
        return;
      }
    } else {
      // New approach: use template object directly
      template = templateOrId;
    }

    try {
      if (replace) {
        await handleClearAllShifts(targetDate);
      }

      const newShifts = applyTemplateToDate(template, targetDate);
      
      // Direct database operations for immediate UI updates
      const shiftsToInsert = newShifts.map(shift => ({
        business_id: businessId,
        start_time: shift.startTime.toISOString(),
        end_time: shift.endTime.toISOString(),
        title: shift.title,
      }));

      const { error: insertError } = await supabaseClient
        .from('shifts')
        .insert(shiftsToInsert);

      if (insertError) throw insertError;

      handleSuccess(`Day template "${template.name}" applied successfully (${newShifts.length} shifts created)`);

      // Trigger refresh to show new shifts immediately
      onShiftCreated?.();
    } catch (error) {
      handleError('apply day template', error);
    }
  }, [businessId, effectiveProps.enableShiftManagement, dayTemplates, handleClearAllShifts, handleError, handleSuccess, onShiftCreated]);

  // Handler for saving day templates
  const handleSaveDayTemplate = useCallback(async (name: string, description: string) => {
    if (onSaveDayTemplate) {
      await onSaveDayTemplate(name, description);
    } else {
      // Default implementation - save to database
      try {
        const currentWorkingDate = selectedEditingDate || currentDisplayMonth;

        // Use unified utility function with correct property access
        const shiftsForDay = getNonHistoricalShiftsForDate(shifts, currentWorkingDate);

        if (shiftsForDay.length === 0) {
          toast.error('No shifts available to save as template');
          return;
        }

        const dayTemplate = {
          business_id: businessId,
          name: name.trim(),
          description: description.trim() || null,
          shifts: shiftsForDay.map(shift => ({
            startTime: format(shift.startTime, 'HH:mm'),
            endTime: format(shift.endTime, 'HH:mm'),
            employee_id: shift.employeeId,
            role_id: (shift as any).role_id,
            breaks: shift.breaks || []
          }))
        };

        const { error } = await supabaseClient
          .from('day_templates')
          .insert([dayTemplate]);

        if (error) {
          throw error;
        }

        handleSuccess(`Day template "${name}" saved successfully`);

      } catch (error) {
        handleError('save day template', error);
      }
    }
  }, [businessId, selectedEditingDate, currentDisplayMonth, shifts, onSaveDayTemplate, handleSuccess, handleError, onShiftCreated]);

  

  // Calculate the vertical offset for a specific day in the calendar
  // REMOVED: calculateDayVerticalOffset - no longer needed with virtualization
  // Virtualized rendering uses day-local coordinates, not month-cumulative offsets

  // **CURSOR FOLLOW FIX**: Handler for cursor movement during template creation
  // Use ref to throttle updates and prevent too many re-renders
  const lastCursorUpdateRef = useRef<number>(0);
  const handleTemplateCreationHover = useCallback((cellTime: Date, rowIndex?: number, dayIndex?: number) => {
    if (!effectiveProps.enableShiftManagement) return;
    if (!isEditingMode) return; // Only allow in edit mode
    if (!templateCreationStartTimeRef.current) {
      return; // Only track when we have a start time
    }

    // Throttle updates to every 16ms (60fps) for smooth performance
    const now = Date.now();
    if (now - lastCursorUpdateRef.current < 16) {
      return;
    }
    lastCursorUpdateRef.current = now;

    // Update cursor position for live preview
    setTemplateCreationCursorTime(cellTime);
  }, [effectiveProps.enableShiftManagement, isEditingMode]);

  // Hour cell interaction handlers
  const handleTemplateCreationClick = useCallback((cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => {
    if (!effectiveProps.enableShiftManagement) return;
    if (!isEditingMode) return; // Only allow in edit mode

    // Check if the date is protected from historical edits
    if (isShiftLocked(cellTime, false)) {
      toast.error('Cannot create shift in the past', {
        description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
      });
      return;
    }

    if (!templateCreationStartTimeRef.current) {
      // **CURSOR FOLLOW FIX**: First click - start template creation and show preview
      setTemplateCreationStartTime(cellTime);
      setTemplateCreationEndTime(null); // No end time yet - will follow cursor
      setTemplateCreationCursorTime(cellTime); // Initialize cursor at click position
      setTemplateCreationRowIndex(rowIndex || 0);
      setTemplateCreationDayIndex(dayIndex !== undefined ? dayIndex : null);
    } else {
      // Second click: Set end time and enter resize mode
      const currentStartTime = templateCreationStartTimeRef.current;
      const endTime = cellTime > currentStartTime ? cellTime : currentStartTime;
      const startTime = cellTime > currentStartTime ? currentStartTime : cellTime;

      setTemplateCreationEndTime(endTime);
      setTemplateCreationStartTime(startTime); // Update to ensure proper ordering
      setTemplateCreationCursorTime(null); // Clear cursor tracking

      // Enter resize mode instead of opening dialog immediately
      setIsResizeMode(true);
    }
  }, [effectiveProps.enableShiftManagement, setTemplateCreationStartTime, isShiftLocked, isEditingMode]);

  const handleOpenInlineBuilder = useCallback((cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => {
    if (!effectiveProps.enableShiftManagement) return;
    if (!isEditingMode) return; // Only allow in edit mode

    // Check if the date is protected from historical edits
    if (isShiftLocked(cellTime, false)) {
      toast.error('Cannot create shift in the past', {
        description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
      });
      return;
    }

    // Show shift dialog for quick shift creation
    setTemplateCreationStartTime(cellTime);
    setTemplateCreationRowIndex(rowIndex || 0);
    setTemplateCreationDayIndex(dayIndex !== undefined ? dayIndex : null);
    setShowShiftDialog(true);
  }, [effectiveProps.enableShiftManagement, isShiftLocked, isEditingMode]);

  // Function to scroll to today
  const handleScrollToToday = useCallback(() => {
    // Use virtualized list scroll if available
    if (virtualizedListRef.current) {
      virtualizedListRef.current.scrollToToday();
      return;
    }

    // Fallback to traditional scroll (backward compatibility)
    const today = new Date();
    const todayKey = format(today, 'yyyy-MM-dd');
    const todayElement = dayRefs.current.get(todayKey);

    if (todayElement && scrollContainerRef.current) {
      todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Function to scroll to a specific date
  const handleScrollToDate = useCallback((date: Date, align?: 'start' | 'center' | 'end') => {
    // Use virtualized list scroll if available
    if (virtualizedListRef.current) {
      virtualizedListRef.current.scrollToDate(date, align || 'start');
      return;
    }

    // Fallback to traditional scroll (backward compatibility)
    const dateKey = format(date, 'yyyy-MM-dd');
    const dateElement = dayRefs.current.get(dateKey);

    if (dateElement && scrollContainerRef.current) {
      dateElement.scrollIntoView({ behavior: 'smooth', block: align === 'center' ? 'center' : 'start' });
    }
  }, []);

  // Template drop handler - memoized to prevent re-renders
  const handleDayTemplateDrop = useCallback((template: DayTemplate, targetDate: Date) => {
    const previewShifts = generatePlanningPreviewShifts(template, targetDate);
    setPlanningPreviewShifts(previewShifts);
    setSelectedTemplateForApplication(template);
    setTargetDateForTemplateApplication(targetDate);
    setIsTemplatePopoverOpen(true);
  }, [generatePlanningPreviewShifts]);

  // Template resize handler - memoized to prevent re-renders
  const handleTemplateResizeComplete = useCallback((startTime: Date, endTime: Date) => {
    setTemplateCreationStartTime(startTime);
    setTemplateCreationEndTime(endTime);
  }, []);

  // Template finalized handler - memoized to prevent re-renders
  const handleTemplateFinalizedClick = useCallback(() => {
    setIsResizeMode(false);
    setShowShiftDialog(true);
  }, []);

  // Get current time in business timezone (hours and minutes)
  // Uses currentTimeRef to avoid rerendering calendar when time updates
  const getCurrentTimeInBusinessTimezone = useCallback(() => {
    const currentTime = currentTimeRef.current;
    try {
      // Use Intl.DateTimeFormat to get hours and minutes in the business timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: businessTimezone,
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
      });

      const parts = formatter.formatToParts(currentTime);
      const hours = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
      const minutes = parseInt(parts.find(part => part.type === 'minute')?.value || '0');

      return { hours, minutes };
    } catch (error) {
      // Fallback to browser timezone
      const fallbackHours = currentTime.getHours();
      const fallbackMinutes = currentTime.getMinutes();

      return {
        hours: fallbackHours,
        minutes: fallbackMinutes
      };
    }
  }, [businessTimezone]);

  // Extract current time as simple numbers to avoid passing unstable callbacks
  // This updates every 60s but won't cause itemProps recreation in VirtualizedDayList
  const currentBusinessTime = getCurrentTimeInBusinessTimezone();
  const currentTimeHours = currentBusinessTime.hours;
  const currentTimeMinutes = currentBusinessTime.minutes;

  // Function to scroll to current time
  const scrollToCurrentTime = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerWidth = container.offsetWidth;
    const scrollableWidth = container.scrollWidth;

    const displayStartMinutes = finalDisplayStartTime.getHours() * 60 + finalDisplayStartTime.getMinutes();
    const totalDisplayMinutes = actualDisplayHours * 60;

    // Get current time in business timezone instead of browser timezone
    const { hours, minutes } = getCurrentTimeInBusinessTimezone();
    const currentTimeMinutes = hours * 60 + minutes;

    let relativeCurrentTimeMinutes = currentTimeMinutes - displayStartMinutes;
    
    if (relativeCurrentTimeMinutes < 0 && actualDisplayHours > 24) {
      relativeCurrentTimeMinutes += 24 * 60;
    }
    
    const currentTimePixelPosition = (relativeCurrentTimeMinutes / totalDisplayMinutes) * (actualDisplayHours * INTERVALS_PER_HOUR * hourCellWidthPx);
    const desiredScrollLeft = currentTimePixelPosition - (containerWidth / 2);
    const maxScrollLeft = scrollableWidth - containerWidth;
    const clampedScrollLeft = Math.max(0, Math.min(desiredScrollLeft, maxScrollLeft));

    container.scrollTo({
      left: clampedScrollLeft,
      behavior: 'smooth',
    });
  }, [getCurrentTimeInBusinessTimezone, finalDisplayStartTime, actualDisplayHours, hourCellWidthPx]);


  // Calculate the position of the current time line specifically for time header
  // This accounts for the day column width when present
  const getTimeHeaderCurrentTimePosition = useCallback(() => {
    if (!hourCellWidthPx || hourCellWidthPx <= 0) {
      return '0px'; // Return safe fallback position
    }

    if (!finalDisplayStartTime || !getCurrentTimeInBusinessTimezone || actualDisplayHours <= 0) {
      return '0px'; // Return safe fallback position
    }

    const displayStartMinutes = finalDisplayStartTime.getHours() * 60 + finalDisplayStartTime.getMinutes();
    const totalDisplayMinutes = actualDisplayHours * 60;

    // Get current time in business timezone instead of browser timezone
    const { hours, minutes } = getCurrentTimeInBusinessTimezone();
    const currentTimeMinutes = hours * 60 + minutes;

    let relativeCurrentTimeMinutes = currentTimeMinutes - displayStartMinutes;

    if (relativeCurrentTimeMinutes < 0 && actualDisplayHours > 24) {
      relativeCurrentTimeMinutes += 24 * 60;
    }

    const clampedCurrentTimeMinutes = Math.max(0, Math.min(totalDisplayMinutes, relativeCurrentTimeMinutes));

    // Calculate pixel position within the time intervals area
    const totalIntervals = Math.ceil(actualDisplayHours * INTERVALS_PER_HOUR);
    const timeIntervalsWidth = totalIntervals * hourCellWidthPx;
    const timePositionPixels = (clampedCurrentTimeMinutes / totalDisplayMinutes) * timeIntervalsWidth;

    // Day column offset removed - CSS Grid handles column spacing
    return `${timePositionPixels}px`;
  }, [getCurrentTimeInBusinessTimezone, finalDisplayStartTime, actualDisplayHours, hourCellWidthPx, effectiveProps.hideDayColumn]);

  // Expose scroll functions and tutorial methods
  useImperativeHandle(ref, () => ({
    scrollToToday: handleScrollToToday,
    scrollToCurrentTime: scrollToCurrentTime,
    scrollToDate: handleScrollToDate,
    startTutorial: startTutorial,
  }));

  // Auto-scroll to current time if enabled
  useEffect(() => {
    if (effectiveProps.autoScrollToCurrentTime) {
      const timer = setTimeout(() => {
        scrollToCurrentTime();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [effectiveProps.autoScrollToCurrentTime, scrollToCurrentTime]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && templateCreationStartTimeRef.current) {
        setTemplateCreationStartTime(null);
        setTemplateCreationEndTime(null);
        setTemplateCreationCursorTime(null); // Clear cursor tracking
        setTemplateCreationRowIndex(null);
        setTemplateCreationDayIndex(null);
        setIsResizeMode(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []); // No dependencies needed since we use ref

  useEffect(() => {
    if (templateCreationStartTime) {
      // Auto-clear template creation state after 30 seconds to prevent permanent blocking
      const timeoutId = setTimeout(() => {
        setTemplateCreationStartTime(null);
        setTemplateCreationEndTime(null);
        setTemplateCreationCursorTime(null); // Clear cursor tracking
        setTemplateCreationRowIndex(null);
        setTemplateCreationDayIndex(null);
        setIsResizeMode(false);
      }, 30000); // 30 second timeout

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [templateCreationStartTime]); // Re-run when template creation starts

  useEffect(() => {
    const clearTemplateCreationState = () => {
      setTemplateCreationStartTime(null);
      setTemplateCreationEndTime(null);
      setTemplateCreationCursorTime(null); // Clear cursor tracking
      setTemplateCreationRowIndex(null);
      setTemplateCreationDayIndex(null);
      setIsResizeMode(false);
    };

    (window as any).clearTemplateCreationState = clearTemplateCreationState;

    return () => {
      delete (window as any).clearTemplateCreationState;
    };
  }, []);


  // Measure actual container height for accurate virtualization (full variant only)
  // Calculate dynamic initial height based on window size minus typical header/padding
  // This provides a reasonable estimate until ResizeObserver measures the actual container
  // Note: insights and timetracking variants don't use virtualization, so height is irrelevant
  const [containerHeight, setContainerHeight] = useState(() => {
    // Only used by full variant for virtualization
    // Account for typical page structure: navbar (~64px) + padding/margins (~100px)
    const estimatedHeight = Math.max(400, window.innerHeight - 164);
    return estimatedHeight;
  });

  const gridTemplateColumns = useMemo(() => {
    const safeTotalDisplayHours = isNaN(actualDisplayHours) || actualDisplayHours <= 0 ? 12 : actualDisplayHours;
    const totalIntervals = Math.ceil(safeTotalDisplayHours * INTERVALS_PER_HOUR);
    const dayColumnWidth = effectiveProps.hideDayColumn ? '' : '32px';

    // Use fixed pixel widths instead of 1fr to prevent scrollbar-related width bounce
    // When scrollbar toggles (appears/disappears ~17px on Windows/Chrome), CSS 1fr columns
    // would immediately recalculate widths causing visible bounce. Fixed pixel widths
    // stay stable because they're based on JS-measured container width which has a 25px
    // threshold that ignores small scrollbar-related width changes.
    // Fall back to 1fr for initial render when hourCellWidthPx is not yet measured.
    const intervalColumns = hourCellWidthPx > 0
      ? `repeat(${totalIntervals}, ${hourCellWidthPx}px)`
      : `repeat(${totalIntervals}, 1fr)`;

    const template = effectiveProps.hideDayColumn ? intervalColumns : `${dayColumnWidth} ${intervalColumns}`;
    return template;
  }, [actualDisplayHours, effectiveProps.hideDayColumn, hourCellWidthPx]);

  // Centralized day row calculations for perfect synchronization
  const dayRowCalculations = useMemo(() => {
    return daysInSelectedMonth.map((dayDate) => {
      const dayOfMonth = getDate(dayDate);
      // Use the same combinedShiftsByDay logic that works perfectly for day labels
      const dailyShifts = combinedShiftsByDay[dayOfMonth] || [];

      // Calculate number of rows needed for this day - same calculation for all cases
      const hasDroppedTemplate = dailyShifts.some(s => s.isPlanningPreview);
      const organizedShiftRows = organizeShiftsIntoRows(dailyShifts);
      const hasShifts = organizedShiftRows.length > 0;
      // Only include extra row in edit mode (for drop zones)
      const numRows = Math.max(1, organizedShiftRows.length + (hasShifts && isEditingMode ? 1 : 0));

      return {
        dayDate,
        dayOfMonth,
        dailyShifts,
        organizedShiftRows,
        numRows,
        hasDroppedTemplate,
        hasShifts: organizedShiftRows.length > 0
      };
    });
  }, [daysInSelectedMonth, combinedShiftsByDay, isEditingMode]);

  // Calculate dayRowOffsets once using the same logic as day labels
  const dayRowOffsets = useMemo(() => {
    const offsets: { [key: number]: number } = {};
    let cumulativeRowOffset = 1;
    
    dayRowCalculations.forEach((dayCalc, index) => {
      offsets[index] = cumulativeRowOffset;
      cumulativeRowOffset += dayCalc.numRows;
      // Add gap row if not the last day
      if (index < dayRowCalculations.length - 1) {
        cumulativeRowOffset += 1;
      }
    });
    
    return offsets;
  }, [dayRowCalculations, combinedShiftsByDay]);

  // Calculate total content height for scrollbar detection
  const totalContentHeight = useMemo(() => {
    return dayRowCalculations.reduce((sum, dayCalc, index) => {
      const isLastDay = index === dayRowCalculations.length - 1;
      // Same formula as useVirtualization: rows * ROW_HEIGHT_PX + gap/padding
      return sum + (dayCalc.numRows * ROW_HEIGHT_PX) + (isLastDay ? ROW_HEIGHT_PX : GAP_HEIGHT_PX);
    }, 0);
  }, [dayRowCalculations]);

  const gridTemplateRows = useMemo(() => {
    const rows = [];
    
    // Time header row is now outside the grid, so don't include it
    
    if (dayRowCalculations.length > 0) {
      dayRowCalculations.forEach((dayCalc, dayIndex) => {
        // Add individual rows for each day - this gives each row its own CSS Grid space
        for (let i = 0; i < dayCalc.numRows; i++) {
          rows.push(`${ROW_HEIGHT_PX}px`);
        }
        
        // Add gap row after each day except the last one
        if (dayIndex < dayRowCalculations.length - 1) {
          rows.push('8px');
        }
      });
    }
    
    const result = rows.length > 0 ? rows.join(' ') : 'auto';
    return result;
  }, [dayRowCalculations]);

  // Only show loading screen when there's no existing data (initial load)
  // Keep showing existing calendar data during refetches for better UX
  const hasExistingData = shifts && shifts.length > 0;
  // Use unified loading state - wait for ALL data before showing calendar
  const isInitialLoading = (!calendarReadiness.isFullyReady || loading) && !hasExistingData;

  // Calendar skeleton component for loading state - adapts to variant/fullHeight
  const CalendarSkeleton = ({ isFullHeight, calendarVariant }: { isFullHeight?: boolean; calendarVariant?: string }) => (
    <div className={cn(
      "flex flex-col gap-1 p-2",
      isFullHeight && calendarVariant === 'full' && "h-full"
    )}>
      {isFullHeight && calendarVariant === 'full' ? (
        // Full height skeleton for shift management - show more rows to fill space
        <>
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded flex-shrink-0" />
          ))}
          <div className="flex-1" /> {/* Spacer to maintain layout */}
        </>
      ) : (
        // Compact skeleton for time tracking and other variants
        [...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-8 w-full rounded" />
        ))
      )}
    </div>
  );

  return (
    <>
      {/* Grid Drag Styles */}
      <style>{GRID_DRAG_STYLES}</style>

      {/* Tutorial Floater Fixed Positioning */}
      <style>{`
        .__floater.__floater__open {
          position: fixed !important;
          bottom: -280px !important;
          right: -280px !important;
          top: auto !important;
          left: auto !important;
          transform: none !important;
          z-index: 999999 !important;
        }
      `}</style>

      {/* Interactive Tutorial */}
      <Joyride
        steps={tutorialSteps}
        run={tutorialRun}
        stepIndex={tutorialStepIndex}
        callback={handleJoyrideCallback}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        disableCloseOnEsc={false}
        disableOverlayClose={false}
        hideBackButton={false}
        scrollToFirstStep={true}
        scrollOffset={120}
        spotlightPadding={8}
        disableScrolling={false}
        disableScrollParentFix={true}
        floaterProps={{
          disableAnimation: true,
          options: {
            preventOverflow: {
              enabled: false,
            },
            flip: {
              enabled: false,
            },
          },
        }}
        styles={{
          options: {
            primaryColor: 'hsl(var(--primary))',
            textColor: 'hsl(var(--foreground))',
            backgroundColor: 'hsl(var(--background))',
            overlayColor: 'rgba(0, 0, 0, 0)',
            arrowColor: 'transparent',
            zIndex: 998888,
          },
          tooltip: {
            background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 60%, transparent 100%)',
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            padding: '2rem',
            width: '600px',
            height: '600px',
            maxWidth: '600px',
            transform: 'rotate(20deg)',
            clipPath: 'polygon(0 0, 100% 0, 0 100%)',
          },
          tooltipContainer: {
            textAlign: 'left',
            transform: 'rotate(-20deg)',
            color: 'hsl(var(--foreground))',
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            padding: '0',
            margin: '0',
            maxWidth: '50%',
            display: 'flex',
            flexDirection: 'column',
          },
          tooltipTitle: {
            color: 'hsl(var(--foreground))',
            background: 'transparent',
            fontSize: '1.125rem',
            fontWeight: '600',
            margin: '0 0 0.5rem 0',
            padding: '0',
          },
          tooltipContent: {
            color: 'hsl(var(--foreground))',
            background: 'transparent',
            fontSize: '0.875rem',
            padding: '0',
            margin: '0',
          },
          buttonNext: {
            backgroundColor: 'hsl(var(--primary))',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
          },
          buttonBack: {
            color: 'hsl(var(--muted-foreground))',
            backgroundColor: 'transparent',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            marginRight: '0.5rem',
            cursor: 'pointer',
          },
          buttonSkip: {
            color: 'hsl(var(--muted-foreground))',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tutorial',
        }}
      />

      <div
        ref={calendarContainerRef}
        className={cn(
          CALENDAR_BASE_CLASSES,
          "flex flex-col",
          fullHeight && CALENDAR_HEIGHT,
          className
        )}
        onClick={onClick}
        data-calendar-container
        data-tutorial="calendar-container"
      >
      {/* Calendar Header */}
      <div 
        ref={calendarHeaderRef}
        className="dark:border-gray-600 rounded-t-lg p-6 pb-3"
      >
        <CalendarHeader
          currentDisplayMonth={currentDisplayMonth}
          displayOnlyDay={displayOnlyDay}
          hidePastDays={effectiveProps.hidePastDays}
          events={events}
          showEvents={showEvents}
          onToggleEvents={onToggleEvents}
          hideControls={effectiveProps.hideControls}
          onPreviousMonth={onPreviousMonth}
          onNextMonth={onNextMonth}
          onSetToday={onSetToday}
          onToggleHidePastDays={onToggleHidePastDays}
          expandAllDays={effectiveProps.expandAllDays}
          onToggleExpandAllDays={onToggleExpandAllDays}
          onSyncCalendar={onSyncCalendar}
          businessName={businessName}
          calendarRef={calendarContainerRef}
          shifts={shifts}
          employees={employees}
          onMonthSelect={onMonthSelect}
          businessCreatedAt={businessCreatedAt}
          areSidePanelsCollapsed={effectiveProps.areSidePanelsCollapsed}
          onToggleSidePanelsCollapsed={onToggleSidePanelsCollapsed}
          openingHours={openingHours}
          variant={variant}
          // Time range control props
          show24Hours={show24Hours}
          onToggleShow24Hours={onToggleShow24Hours}
          // Infinite scroll mode props
          scrollMode={scrollMode}
          // Simple display hours props (from simpleDisplayHours hook in context)
          smartDisplayHours={smartDisplayHours}
          // Actions panel props
          isActionsPanelCollapsed={isActionsPanelCollapsed}
          onToggleActionsPanel={onToggleActionsPanel}
          isEditingMode={isEditingMode}
          // Tutorial callback
          onStartInteractiveTutorial={startTutorial}
          // Control ref for tutorial
          controlRef={calendarHeaderControlRef}
        />
      </div>

      {/* Main Content Container */}
      <div className="relative flex flex-col w-full min-w-0 max-w-full overflow-hidden px-2 pb-2" ref={cardContentRef}>

        {/* Fixed Time Headers Row */}
        {!effectiveProps.hideTimeHeader && (
          <div className="flex w-full flex-nowrap">
            <div
              className="flex-1 min-w-0 overflow-x-hidden overflow-y-scroll time-header-scroll-sync hide-scrollbar"
              ref={timeHeaderScrollRef => {
                // Sync horizontal scroll between time headers and main calendar
                const syncScroll = (e: Event) => {
                  if (timeHeaderScrollRef && scrollContainerRef.current) {
                    timeHeaderScrollRef.scrollLeft = scrollContainerRef.current.scrollLeft;
                  }
                };
                
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.removeEventListener('scroll', syncScroll);
                  scrollContainerRef.current.addEventListener('scroll', syncScroll);
                }
              }}
            >
              <div 
                className="grid w-full relative"
                style={{ 
                  gridTemplateColumns: gridTemplateColumns,
                  gridTemplateRows: '40px',
                  rowGap: '0',
                  columnGap: '0'
                }}
              >
                {/* Day Column Header - shows current month in infinite scroll mode with iOS-style push animation */}
                {!effectiveProps.hideDayColumn && (
                  <div
                    className="p-1 border-b dark:border-gray-600 flex items-center justify-center overflow-hidden"
                    style={{
                      gridColumn: 1,
                      gridRow: 1,
                      minHeight: '40px'
                    }}
                  >
                    {isInfiniteScrollMode ? (
                      <div className="relative h-full w-full flex items-center justify-center">
                        {/* Current month - slides up when pushed */}
                        <div
                          className="flex flex-col items-center leading-tight"
                          style={{
                            transform: `translateY(${stickyMonthOffset}px)`,
                            opacity: stickyMonthOffset === 0 ? 1 : Math.max(0, 1 + stickyMonthOffset / MONTH_DIVIDER_VISUAL_HEIGHT_PX),
                          }}
                        >
                          <span className="text-xs font-semibold">{format(visibleMonth, 'MMM')}</span>
                          <span className="text-[10px] text-muted-foreground">{format(visibleMonth, 'yyyy')}</span>
                        </div>
                        {/* Next month - slides in from below when pushing */}
                        {stickyMonthOffset < 0 && (
                          <div
                            className="absolute flex flex-col items-center leading-tight"
                            style={{
                              transform: `translateY(${MONTH_DIVIDER_VISUAL_HEIGHT_PX + stickyMonthOffset}px)`,
                              opacity: Math.min(1, -stickyMonthOffset / MONTH_DIVIDER_VISUAL_HEIGHT_PX),
                            }}
                          >
                            <span className="text-xs font-semibold">{format(addMonths(visibleMonth, 1), 'MMM')}</span>
                            <span className="text-[10px] text-muted-foreground">{format(addMonths(visibleMonth, 1), 'yyyy')}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm font-medium">Day</span>
                    )}
                  </div>
                )}

                {/* Current Time Indicator in Time Header */}
                {hourCellWidthPx > 0 && (
                  <div
                    className={`absolute bg-red-500 h-full w-0.5 pointer-events-none ${getZIndexClass('CURRENT_TIME_LINE')}`}
                    style={{
                      left: getTimeHeaderCurrentTimePosition(),
                      top: 0,
                      height: '40px'
                    }}
                  />
                )}

                {/* Time Interval Headers - Use TimeHeader component for consistency */}
                <TimeHeader
                  totalDisplayHours={actualDisplayHours}
                  effectiveDisplayStartTime={finalDisplayStartTime}
                  hideDayColumn={effectiveProps.hideDayColumn}
                  show24Hours={show24Hours}
                />

              </div>
            </div>
            
            {/* Actions Header - aligned with time headers */}
            {!effectiveProps.hideSidePanels && !isActionsPanelCollapsed && (
              <div 
                className="text-sm font-medium flex-shrink-0 border-l border-b dark:border-gray-600 flex items-center justify-center overflow-hidden"
                style={{ 
                  width: '9rem'
                }}
              >
                <div>
                  Actions
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unified Content Row */}
        <div className="calendar-content-row flex w-full overflow-hidden flex-1 relative">

          {/* Calendar Container */}
          <div
            className="flex-1 min-w-0 relative overflow-hidden"
            ref={scrollContainerCallbackRef}
          >
            {isInitialLoading ? (
              <CalendarSkeleton isFullHeight={fullHeight} calendarVariant={variant} />
            ) : variant === 'full' ? (
              // Use virtualization for month view (full variant)
              <VirtualizedDayList
                ref={virtualizedListRef}
                daysInSelectedMonth={daysInSelectedMonth}
                dayRowCalculations={dayRowCalculations}
                dayRowOffsets={dayRowOffsets}
                shifts={shifts}
                shiftsByDay={shiftsByDay}
                shiftsByDayOrganized={shiftsByDayOrganized}
                employees={employees}
                totalDisplayHours={actualDisplayHours}
                effectiveDisplayStartTime={finalDisplayStartTime}
                effectiveDisplayEndTime={finalDisplayEndTime}
                hourCellWidthPx={hourCellWidthPx}
                gridTemplateRows={gridTemplateRows}
                gridTemplateColumns={gridTemplateColumns}
                businessId={businessId}
                countryCode={countryCode}
                openingHoursByDayOfWeek={openingHoursByDayOfWeek}
                closedDatesMap={closedDatesMap}
                workingHours={workingHours}
                hideDayColumn={effectiveProps.hideDayColumn}
                hideActionsColumn={effectiveProps.hideActionsColumn}
                isEditingMode={isEditingMode}
                show24Hours={show24Hours}
                expandedDayKeys={expandedDayKeys}
                selectedEditingDate={selectedEditingDate}
                onDaySelect={onDaySelect}
                onAssignEmployee={onAssignEmployee}
                onCreateShift={effectiveProps.enableShiftManagement ? handleCreateShift : undefined}
                onRepositionShift={effectiveProps.enableShiftManagement ? handleRepositionShift : undefined}
                onShiftSelect={onShiftSelect}
                onDeleteShift={onDeleteShift}
                selectedShiftIds={selectedShiftIds}
                actionsPanelMode={unifiedActionsPanelMode}
                onOpenActionsPanel={onToggleActionsPanel}
                onSetActionsPanelMode={onUnifiedActionsPanelModeChange}
                isActionsPanelOpen={!isActionsPanelCollapsed}
                onTemplateCreationClick={handleTemplateCreationClick}
                onTemplateCreationHover={handleTemplateCreationHover}
                onOpenInlineBuilder={handleOpenInlineBuilder}
                isTemplateCreationInProgress={!!templateCreationStartTime}
                onDayTemplateDrop={effectiveProps.enableShiftManagement ? handleDayTemplateDrop : undefined}
                templateCreationStartTime={templateCreationStartTime}
                templateCreationEndTime={templateCreationEndTime}
                templateCreationCursorTime={templateCreationCursorTime}
                templateCreationDayIndex={templateCreationDayIndex}
                templateCreationRowIndex={templateCreationRowIndex}
                isResizeMode={isResizeMode}
                onTemplateResizeComplete={handleTemplateResizeComplete}
                onTemplateFinalizedClick={handleTemplateFinalizedClick}
                checkIsHoliday={checkIsHoliday}
                currentTimeHours={currentTimeHours}
                currentTimeMinutes={currentTimeMinutes}
                getDisplayTimesForDay={getDisplayTimesForDay}
                getDayStatusByKey={getDayStatusByKey}
                showEvents={showEvents}
                eventPositionsWithLayout={eventPositionsWithLayout}
                eventsLookup={eventsLookup}
                containerHeight={containerHeight}
                scrollContainerRef={scrollContainerRef}
                isHistoricalEditEnabled={isHistoricalEditEnabled}
                hideStatusIndicators={effectiveProps.hideStatusIndicators}
                onVisibleMonthChange={isInfiniteScrollMode ? handleVisibleMonthChange : undefined}
                onStickyMonthUpdate={isInfiniteScrollMode ? handleStickyMonthUpdate : undefined}
              />
            ) : (
              // Simple non-virtualized rendering for single-day variants (insights, timetracking)
              (() => {
                // Get the single day to display
                const dayDate = daysInSelectedMonth[0];
                const dayCalc = dayRowCalculations[0];

                if (!dayDate || !dayCalc) {
                  return <div className="p-4 text-muted-foreground">No data to display</div>;
                }

                const dayOfMonth = dayCalc.dayOfMonth;
                const dayKey = format(dayDate, 'yyyy-MM-dd');
                const isToday = isSameDay(dayDate, new Date());
                const isWeekendDay = isWeekend(dayDate);
                const holiday = countryCode && checkIsHoliday ? checkIsHoliday(dayDate) : null;
                const isCurrentlyEditing = selectedEditingDate ? isSameDay(dayDate, selectedEditingDate) : false;

                // Get organized shifts for this day
                const organizedShifts = shiftsByDayOrganized[dayOfMonth] || [];

                // Calculate opening hours status
                const dayOfWeekNum = getDay(dayDate);
                const openingHoursForDay = openingHoursByDayOfWeek?.get(dayOfWeekNum);
                const hasOpeningHoursData = openingHoursByDayOfWeek?.size > 0;
                const isDayClosedAllDay = hasOpeningHoursData && (!openingHoursForDay?.is_open || openingHoursForDay?.is_closed_all_day === true);

                // Day status classes
                const getDayStatusClasses = () => {
                  if (isCurrentlyEditing) return `${EDITING_COLORS.BG_SOLID} ${EDITING_COLORS.TEXT_ON_DARK}`;
                  if (isToday) return 'bg-orange-500 text-white';
                  if (isDayClosedAllDay) return 'bg-gray-400 dark:bg-gray-500 text-white';
                  if (holiday && holiday.is_non_working_day) return 'bg-destructive text-destructive-foreground';
                  if (isWeekendDay && !isDayClosedAllDay) return 'bg-primary text-primary-foreground';
                  return 'bg-background text-foreground';
                };

                // Get preparation status for this day (unified single source of truth)
                const dayValidationStatus = getDayStatusByKey ? getDayStatusByKey(dayKey) : null;
                const statusBorderClass = dayValidationStatus
                  ? getPreparationStatusClass(dayValidationStatus.status, 'border')
                  : 'border-l-2 border-l-gray-300 dark:border-l-gray-600';

                // Calculate current time line position
                const getCurrentTimePosition = (): string => {
                  if (!hourCellWidthPx || hourCellWidthPx <= 0 || !finalDisplayStartTime || actualDisplayHours <= 0) {
                    return '0px';
                  }

                  const displayStartMinutes = finalDisplayStartTime.getHours() * 60 + finalDisplayStartTime.getMinutes();
                  const totalDisplayMinutes = actualDisplayHours * 60;
                  const totalCurrentTimeMinutes = currentTimeHours * 60 + currentTimeMinutes;

                  let relativeCurrentTimeMinutes = totalCurrentTimeMinutes - displayStartMinutes;

                  if (relativeCurrentTimeMinutes < 0 && actualDisplayHours > 24) {
                    relativeCurrentTimeMinutes += 24 * 60;
                  }

                  const clampedCurrentTimeMinutes = Math.max(0, Math.min(totalDisplayMinutes, relativeCurrentTimeMinutes));
                  const totalIntervals = Math.ceil(actualDisplayHours * INTERVALS_PER_HOUR);
                  const timeIntervalsWidth = totalIntervals * hourCellWidthPx;
                  const timePositionPixels = (clampedCurrentTimeMinutes / totalDisplayMinutes) * timeIntervalsWidth;

                  return `${timePositionPixels}px`;
                };

                const gridTemplateRowsForDay = `repeat(${dayCalc.numRows}, ${ROW_HEIGHT_PX}px)`;

                return (
                  <div
                    className="shift-calendar-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridTemplateColumns,
                      gridTemplateRows: gridTemplateRowsForDay,
                      rowGap: 0,
                      columnGap: 0,
                      position: 'relative',
                      width: '100%',
                    }}
                    data-calendar-grid
                    data-tutorial="time-grid"
                  >
                    {/* Day Label Column */}
                    {!effectiveProps.hideDayColumn && (
                      <div
                        className={`flex flex-col items-center justify-center cursor-pointer select-none sticky left-0 z-10 ${
                          isCurrentlyEditing ? `border-2 ${EDITING_COLORS.BORDER_SOLID}` : 'border-t border-b border-t-border border-b-border'
                        } ${statusBorderClass} ${getDayStatusClasses()}`}
                        data-day-column
                        data-tutorial="day-column"
                        style={{
                          gridColumn: 1,
                          gridRow: `1 / ${dayCalc.numRows + 1}`,
                          width: '32px',
                          minWidth: '32px',
                          height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
                        }}
                        onClick={() => onDaySelect?.(dayDate)}
                        title={holiday ? holiday.name : undefined}
                      >
                        <div className="text-xs opacity-75 font-normal">{format(dayDate, 'EEE')}</div>
                        <div className="text-base font-semibold">{dayOfMonth}</div>
                        {holiday && (
                          <div className="text-xs mt-1 text-center opacity-90">
                            <Calendar className="h-3 w-3" />
                          </div>
                        )}
                        {hasOpeningHoursData && openingHoursForDay?.is_24_hours && !holiday && (
                          <div className="text-xs mt-1 text-center opacity-90 font-bold">24</div>
                        )}
                      </div>
                    )}

                    {/* Day Rows */}
                    {Array.from({ length: dayCalc.numRows }, (_, rowIndex) => {
                      const gridRowIndex = rowIndex + 1;
                      const isFirstRowInDay = rowIndex === 0;
                      const isLastRowInDay = rowIndex === dayCalc.numRows - 1;

                      return (
                        <DayRow
                          key={`day-row-${dayKey}-${rowIndex}`}
                          dayDate={dayDate}
                          dayIndex={0}
                          dayOfMonth={dayOfMonth}
                          dayKey={dayKey}
                          rowIndex={rowIndex}
                          gridRowIndex={gridRowIndex}
                          isToday={isToday}
                          isWeekendDay={isWeekendDay}
                          holiday={holiday}
                          isCurrentlyEditing={isCurrentlyEditing}
                          businessId={businessId}
                          employees={employees}
                          totalDisplayHours={actualDisplayHours}
                          effectiveDisplayStartTime={finalDisplayStartTime}
                          effectiveDisplayEndTime={finalDisplayEndTime}
                          hourCellWidthPx={hourCellWidthPx}
                          hideDayColumn={effectiveProps.hideDayColumn}
                          isEditingMode={isEditingMode}
                          show24Hours={show24Hours}
                          openingHoursByDayOfWeek={openingHoursByDayOfWeek}
                          onCreateShift={effectiveProps.enableShiftManagement ? handleCreateShift : undefined}
                          onRepositionShift={effectiveProps.enableShiftManagement ? handleRepositionShift : undefined}
                          onAssignEmployee={onAssignEmployee}
                          allDayShifts={shifts}
                          isFirstRowInDay={isFirstRowInDay}
                          isLastRowInDay={isLastRowInDay}
                          isTemplateCreationInProgress={false}
                        />
                      );
                    })}

                    {/* Virtualized Shift Layer */}
                    <VirtualizedShiftLayer
                      dayDate={dayDate}
                      dayIndex={0}
                      dayOfMonth={dayOfMonth}
                      dayKey={dayKey}
                      dayRowOffset={0}
                      organizedShifts={organizedShifts}
                      numRows={dayCalc.numRows}
                      isToday={isToday}
                      shifts={shifts}
                      shiftsByDay={shiftsByDay}
                      employees={employees}
                      totalDisplayHours={actualDisplayHours}
                      effectiveDisplayStartTime={finalDisplayStartTime}
                      effectiveDisplayEndTime={finalDisplayEndTime}
                      hourCellWidthPx={hourCellWidthPx}
                      businessId={businessId}
                      hideDayColumn={effectiveProps.hideDayColumn}
                      onAssignEmployee={onAssignEmployee}
                      onRepositionShift={effectiveProps.enableShiftManagement ? handleRepositionShift : undefined}
                      selectedShiftIds={selectedShiftIds}
                      onShiftSelect={onShiftSelect}
                      onDeleteShift={onDeleteShift}
                    />

                    {/* Closed Day Badge */}
                    {isDayClosedAllDay && dayCalc.dailyShifts.length === 0 && (
                      <div
                        className="absolute inset-0 pointer-events-none flex items-center justify-center"
                        style={{ zIndex: 7, left: 0 }}
                      >
                        <div className="bg-gray-600/80 dark:bg-gray-400/80 text-white dark:text-gray-900 px-2 py-1 rounded text-xs font-medium">
                          CLOSED
                        </div>
                      </div>
                    )}

                    {/* Current Time Indicator */}
                    {isToday && (
                      <div
                        className="absolute bg-red-500 w-0.5 pointer-events-none current-time-line"
                        style={{
                          left: getCurrentTimePosition(),
                          top: 0,
                          height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
                          zIndex: 5,
                        }}
                      />
                    )}
                  </div>
                );
              })()
            )}

            {/* Virtual Drag Layer for smooth drag previews */}
            {variant === 'full' && (
              <VirtualDragLayer
                scrollContainerRef={scrollContainerRef}
                hideDayColumn={effectiveProps.hideDayColumn}
                totalDisplayHours={actualDisplayHours}
                hourCellWidthPx={hourCellWidthPx}
              />
            )}
          </div>

          {/* OLD: Template Creation Overlay moved into VirtualizedDayItem for proper local coordinates */}

          {/* IMPORTANT: Old non-virtualized rendering code removed */}
          {/* If you need to temporarily disable virtualization, add a feature flag here */}

          {/* Old code would be here - now replaced by VirtualizedDayList above */}
          {/* This comment marks where the old grid rendering (1500+ lines) was removed */}


          {/* Actions Panel */}
          {!effectiveProps.hideSidePanels && (
            <div
              className={`actions-panel-container border-l dark:border-gray-600 overflow-hidden w-36 flex-shrink-0 ${
                isActionsPanelCollapsed ? 'panel-collapsed' : 'panel-expanded'
              }`}
            >
              <div className="w-36 h-full">
                <ScrollArea className="w-full h-full">
                  <UnifiedActionsPanel
                  selectedMode={unifiedActionsPanelMode || 'employees'}
                  onModeChange={onUnifiedActionsPanelModeChange || (() => {})}
                  businessId={businessId || ''}
                  selectedDate={selectedEditingDate || currentDisplayMonth}
                  employees={employees}
                  allDayShifts={shifts}
                  onEmployeeDragStart={() => {}}
                  onEmployeeDragEnd={() => {}}
                  shiftTemplates={shiftTemplates || []}
                  dayTemplates={dayTemplates || []}
                  loadingShiftTemplates={loadingShiftTemplates || false}
                  loadingDayTemplates={loadingDayTemplates || false}
                  onEditShiftTemplate={onEditShiftTemplate || (() => {})}
                  onDeleteShiftTemplate={onDeleteShiftTemplate || (() => Promise.resolve())}
                  onEditDayTemplate={onEditDayTemplate || (() => {})}
                  onDeleteDayTemplate={onDeleteDayTemplate || (() => {})}
                  onApplyDayTemplate={handleApplyDayTemplate}
                  onToggleCollapsed={onToggleActionsPanel}
                  // Props for day template application prompt
                  selectedTemplateForApplication={selectedTemplateForApplication}
                  targetDateForTemplateApplication={targetDateForTemplateApplication}
                  isTemplatePopoverOpen={isTemplatePopoverOpen}
                  onTemplatePopoverOpenChange={(isOpen) => {
                    setIsTemplatePopoverOpen(isOpen);
                    if (!isOpen) {
                      clearPlanningPreviews();
                    }
                  }}
                  onClearSelectedTemplate={() => {
                    setSelectedTemplateForApplication(null);
                    setTargetDateForTemplateApplication(null);
                    clearPlanningPreviews();
                  }}
                  // Props for group actions
                  selectedEditingDate={selectedEditingDate}
                  isEditingMode={isEditingMode}
                  onDaySelect={onDaySelect}
                  onClearAllShifts={effectiveProps.enableShiftManagement ? handleClearAllShifts : undefined}
                  onShiftCreated={onShiftCreated}
                  onSaveDayTemplate={handleSaveDayTemplate}
                  // Shift editing mode props
                  selectedShiftIds={selectedShiftIds}
                  onShiftSelect={onShiftSelect}
                  onSelectAllShifts={onSelectAllShifts}
                  onClearShiftSelection={onClearShiftSelection}
                  onEditShift={onEditShift}
                  onDeleteShift={onDeleteShift}
                  onCopyShifts={onCopyShifts}
                  onSaveShiftsAsTemplate={onSaveShiftsAsTemplate}
                  // Opening hours for closed date display (DayGroupActions uses context directly)
                  openingHours={openingHours}
                  />
                </ScrollArea>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Shift Creation Dialogs */}
      {effectiveProps.enableShiftManagement && (
        <>
          {/* Shift Builder Dialog */}
          <ShiftBuilderDialog
            open={showShiftDialog}
            startTime={templateCreationStartTime || new Date()}
            predefinedEndTime={templateCreationEndTime} // Pass the predefined end time
            onSave={(endTime, title, employeeId) => {
              const finalEndTime = templateCreationEndTime || endTime; // Use predefined or calculated end time
              if (businessId && templateCreationStartTime) {
                handleCreateShift(businessId, templateCreationStartTime, finalEndTime, title || 'New Shift');
              }
              setShowShiftDialog(false);
              setTemplateCreationStartTime(null);
              setTemplateCreationEndTime(null);
              setTemplateCreationRowIndex(null);
              setTemplateCreationDayIndex(null);
            }}
            onCancel={() => {
              setShowShiftDialog(false);
              setTemplateCreationStartTime(null);
              setTemplateCreationEndTime(null);
              setTemplateCreationRowIndex(null);
              setTemplateCreationDayIndex(null);
            }}
            employees={employees}
            templates={shiftTemplates || []}
            recentTemplates={[]} // TODO: Implement recent templates functionality
            allDayShifts={shifts}
          />

          {/* Template Creation Dialog */}
          <TemplateCreationDialog
            isOpen={showQuickCreateDialog}
            startTime={templateCreationStartTime}
            endTime={templateCreationEndTime}
            onSave={(name, durationMinutes) => {
              if (businessId && templateCreationStartTime && templateCreationEndTime) {
                handleCreateShift(businessId, templateCreationStartTime, templateCreationEndTime, name || 'New Shift');
              }
              setShowQuickCreateDialog(false);
              setTemplateCreationStartTime(null);
              setTemplateCreationEndTime(null);
              setTemplateCreationRowIndex(null);
              setTemplateCreationDayIndex(null);
            }}
            onCancel={() => {
              setShowQuickCreateDialog(false);
              setTemplateCreationStartTime(null);
              setTemplateCreationEndTime(null);
              setTemplateCreationRowIndex(null);
              setTemplateCreationDayIndex(null);
            }}
          />
        </>
      )}

    </div>
    </>
  );
});

BaseShiftCalendar.displayName = 'BaseShiftCalendar';

export default BaseShiftCalendar;